import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Send, Users, Loader2, History } from "lucide-react";

/**
 * Phase 25 — Admin Push Notifications.
 * Send a Web Push to all opted-in subscribers in one click. Campaign history
 * shows each blast's reach + send/fail counts.
 */
export default function AdminPushPage() {
  const [subs, setSubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", url: "/", image: "" });
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/push/subscribers"),
        api.get("/admin/push/campaigns"),
      ]);
      setSubs(s.data.items || []);
      setCampaigns(c.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) { toast.error("Title + body required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/push/send", { ...form, dry_run: false });
      toast.success(`Sent to ${data.sent}/${data.target_count} subscribers`);
      setForm({ title: "", body: "", url: "/", image: "" });
      await refresh();
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-push-page">
      <header className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
          <Bell className="w-5 h-5 text-indigo-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Push notifications</h1>
          <p className="text-sm text-stone-500">
            Send a notification to every opted-in customer instantly — perfect for daily specials, last-tables, weather pushes.
          </p>
        </div>
      </header>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <Stat icon={<Users className="w-4 h-4" />} label="Active subscribers" value={subs.length} />
        <Stat icon={<Send className="w-4 h-4" />} label="Campaigns sent" value={campaigns.length} />
        <Stat icon={<History className="w-4 h-4" />} label="Total delivered"
              value={campaigns.reduce((a, c) => a + (c.sent || 0), 0)} />
      </div>

      <form onSubmit={send} className="bg-white border border-stone-200 rounded-2xl p-5 mb-8 space-y-4" data-testid="push-compose">
        <div>
          <label className="text-xs uppercase tracking-widest text-stone-500">Title</label>
          <input
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={80} required
            placeholder="Wood-fired pizza is back tonight"
            className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="push-title"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-stone-500">Message</label>
          <textarea
            value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            maxLength={240} required rows={3}
            placeholder="$5 off any pizza until 9pm — tap to order"
            className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm resize-none"
            data-testid="push-body"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Tap URL</label>
            <input
              value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
              data-testid="push-url"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Image URL (optional)</label>
            <input
              value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
              placeholder="https://..."
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
              data-testid="push-image"
            />
          </div>
        </div>
        <button
          type="submit" disabled={sending || subs.length === 0 || !form.title || !form.body}
          data-testid="push-send"
          className="inline-flex items-center gap-2 bg-indigo-600 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {subs.length === 0 ? "Waiting for subscribers" : `Send to ${subs.length} ${subs.length === 1 ? "subscriber" : "subscribers"}`}
        </button>
      </form>

      <div>
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Campaign history</h2>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto my-10" /> :
         campaigns.length === 0 ? (
           <div className="text-sm text-stone-500 italic">No campaigns yet.</div>
         ) : (
          <div className="overflow-x-auto bg-white border border-stone-200 rounded-2xl">
            <table className="w-full text-sm" data-testid="push-history-table">
              <thead className="bg-stone-50 text-stone-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5">When</th>
                  <th className="text-left px-4 py-2.5">Title</th>
                  <th className="text-right px-4 py-2.5">Target</th>
                  <th className="text-right px-4 py-2.5">Sent</th>
                  <th className="text-right px-4 py-2.5">Failed</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-t border-stone-100">
                    <td className="px-4 py-2.5 text-stone-500">{new Date(c.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-stone-900">{c.title}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-stone-500">{c.target_count}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{c.sent}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-rose-600">{c.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-stone-500 text-[11px] uppercase tracking-widest">{icon}{label}</div>
      <div className="text-2xl font-semibold mt-1 text-stone-900">{value}</div>
    </div>
  );
}
